import { useState } from "react";
import { Eye, EyeOff, Users, Bell, BarChart3, Cloud } from "lucide-react";

export default function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username,
          password,
        }),
      });

      const json = await res.json();
      // console.log(json);

      if (!res.ok || json.code !== 0) {
        throw new Error(json.msg || "Login failed");
      }

      // ✅ เก็บ token
      localStorage.setItem("token", json.token);
      localStorage.setItem(
        "user",
        JSON.stringify(json.user)
      );

      window.location.href = json.user.role === "superadmin" ? "/superadmin" : "/customer-flow";

    } catch (err) {
      alert("ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง");
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: Users,
      title: "Customer Analytics",
      description: "วิเคราะห์ข้อมูลลูกค้า เพศ อายุ Member และ VIP แบบละเอียด",
    },
    {
      icon: Bell,
      title: "LINE Notify",
      description: "แจ้งเตือนทันทีเมื่อ VIP ผ่าน LINE",
    },
    {
      icon: BarChart3,
      title: "Business Dashboard",
      description: "Dashboard ใช้ง่าย real time รองรับ Mobile",
    },
    {
      icon: Cloud,
      title: "Oncloud",
      description: "เชื่อมต่อทุกข้อมูลผ่าน Cloud ทุกที่ทุกเวลา",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden bg-gradient-to-b from-white via-cyan-50/30 to-white">

      {/* Glow Background */}
      <div className="absolute -top-40 -right-40 w-80 h-80 bg-cyan-100/50 rounded-full blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-teal-100/50 rounded-full blur-3xl" />

      <div className="relative z-10 w-full max-w-md px-6">

        {/* Branding */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="relative w-14 h-14 rounded-full shadow-xl">
              <div className="absolute inset-0 rounded-full bg-cyan-400/30 blur-lg"></div>
              <img
                src="/pixels-logo-circle.png"
                alt="PIXELS Logo"
                className="relative w-full h-full object-cover rounded-full border border-white"
              />
            </div>
            <h1 className="text-3xl font-bold text-slate-900">PIXELS AI</h1>
          </div>
          <p className="text-[#00b3b4] text-sm font-semibold tracking-wide">
            Data Business Platform
          </p>
        </div>

        {/* Card */}
        <div className="border border-cyan-200/60 bg-white/95 backdrop-blur-md shadow-2xl rounded-xl p-8">

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              ลงชื่อเข้าใช้
            </h2>
            <p className="text-slate-500 text-sm">
              เข้าสู่ระบบ PIXELS AI Data Business
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">

            <div>
              <label className="text-slate-700 font-semibold text-sm">
                ชื่อผู้ใช้
              </label>
              <input
                type="text"
                placeholder="กรอกชื่อผู้ใช้"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="mt-2 w-full border-2 border-cyan-200 rounded-lg px-4 py-2 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/30 transition-all"
                required
              />
            </div>

            <div>
              <label className="text-slate-700 font-semibold text-sm">
                รหัสผ่าน
              </label>
              <div className="relative mt-2">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="กรอกรหัสผ่าน"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border-2 border-cyan-200 rounded-lg px-4 py-2 pr-10 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/30 transition-all"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-cyan-600"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white font-semibold py-3 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              {isLoading ? "กำลังเข้าสู่ระบบ..." : "เข้าสู่ระบบ"}
            </button>

          </form>
        </div>

        <div className="mt-6 flex items-center justify-center gap-2 text-slate-500 text-xs">
          🔒 การเชื่อมต่อที่ปลอดภัย 256-bit SSL
        </div>
      </div>

      {/* Features */}
      <div className="relative z-10 w-full mt-20 px-6 pb-12">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <div key={i} className="bg-white/90 backdrop-blur-sm border border-cyan-200/50 rounded-xl p-6 hover:shadow-xl transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-100 to-teal-100 rounded-lg flex items-center justify-center">
                    <Icon className="w-6 h-6 text-cyan-600" />
                  </div>
                  <h4 className="font-bold text-slate-900 text-sm">
                    {feature.title}
                  </h4>
                </div>
                <p className="text-slate-600 text-sm">
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
}
